import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useCreateLibrary, useImportSymbols, useSearchLibraries } from '../../hooks/api/use-symbols.hook';
import { ImportSymbolForm } from './ImportSymbolForm';
import { SymbolType } from '../../../../types/constants/symbol-type';

export const ImportSymbols = () => {
  const navigate = useNavigate();

  const [importSymbols, { error: importSymbolsError }] = useImportSymbols();
  const [createLibrary, { error: createLibraryError }] = useCreateLibrary();
  const { loading: loadingLibraries, response: librariesResponse, error: librariesError } = useSearchLibraries({});

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ImportSymbolForm>({
    resolver: classValidatorResolver(ImportSymbolForm, {
      transformer: { targetMaps: [{ target: ImportSymbolForm, properties: { file: Object } }] },
    }),
  });

  const onSubmit: SubmitHandler<ImportSymbolForm> = async ({ library, file }) => {
    if (!library.id) {
      const createdLibrary = await createLibrary({
        name: library.name,
        type: file.name.endsWith('.ltp') ? SymbolType.LINE : SymbolType.SYMBOL,
      });
      await importSymbols({ libraryId: createdLibrary.id, file });
    } else {
      await importSymbols({ libraryId: library.id, file });
    }

    navigate('..');
  };

  const renderContent = () => {
    if (librariesError || createLibraryError) {
      return <Alert severity="error">{(librariesError || createLibraryError)?.response?.data.message}</Alert>;
    }

    return (
      <>
        <Controller
          control={control}
          name="library"
          render={({ field }) => (
            <Autocomplete
              {...register('library')}
              freeSolo
              loading={loadingLibraries}
              sx={{ mt: '1rem', mb: '1rem' }}
              onChange={(_event, option) => field.onChange(typeof option === 'string' ? { name: option } : option)}
              onBlur={field.onBlur}
              value={field.value}
              getOptionLabel={(library) => (typeof library === 'string' ? library : library.name)}
              options={librariesResponse?.pages.map(({ result }) => result).flat() ?? []}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Library"
                  error={Boolean(errors.library)}
                  helperText={
                    errors.library?.message ||
                    (!field.value?.id && field.value?.name ? 'A new library with this name will be created' : '')
                  }
                />
              )}
            />
          )}
        />
        <Controller
          control={control}
          name="file"
          render={({ field }) => (
            <input type="file" onChange={(event) => field.onChange(event.target.files?.[0])} accept=".sym, .ltp" />
          )}
        />
      </>
    );
  };

  return (
    <Dialog open={true}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>Import Symbols / Lines</DialogTitle>
        <DialogContent>
          {renderContent()}
          {importSymbolsError ? (
            <Alert sx={{ mt: '1rem' }} severity="error">
              {importSymbolsError.response?.data?.message}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button disabled={isSubmitting} variant="outlined" onClick={() => navigate('..')} color="primary">
            Cancel
          </Button>
          <LoadingButton color="primary" type="submit" variant="contained" loading={isSubmitting}>
            Import
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};
